export const KNOWLEDGE_QUIZ_PASS_PERCENT = 80;
const QUIZ_LOCK_KEY = 'sds_active_quiz';

interface QuizArticle {
  id: string;
  title: string;
  steps: string[];
  notes?: string;
}

export interface KnowledgeQuizQuestion {
  id: string;
  topic: string;
  prompt: string;
  choices: string[];
  answer: string;
}

const shuffle = <T,>(items: T[]): T[] => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const unique = (items: string[]) => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const value = item.trim();
    if (!value || seen.has(value)) return false;
    seen.add(value);
    return true;
  });
};

type Swap = {
  pattern: RegExp;
  replace: string | ((match: string, ...groups: string[]) => string);
};

const IMPERATIVE =
  /^(tap|scan|sign|notify|capture|add|place|check|review|take|select|open|follow|type|use|acknowledge|prioritize|wear|leave|hand-deliver|have|make|keep|call|wait|enable|turn|set|pull|save|submit|inspect|arrive)\b/i;

const PHRASE_SWAPS: Swap[] = [
  { pattern: /your assigned driver credentials/i, replace: "a coworker's login" },
  { pattern: /assigned delivery window and stop sequence/i, replace: 'first stop only' },
  { pattern: /assigned stop sequence/i, replace: 'closest stops in any order' },
  { pattern: /"Manifest" or "My Stops"/i, replace: '"Settings"' },
  { pattern: /your current shift/i, replace: "yesterday's shift" },
  { pattern: /"Arrive \/ Pickup" or "Arrive \/ Deliver"/i, replace: '"Skip Stop"' },
  { pattern: /upon arriving at a stop/i, replace: 'before you leave the previous stop' },
  { pattern: /use the manual entry option to type the tracking\/manifest number/i, replace: 'skip that package and mark it delivered' },
  {
    pattern: /pull down to refresh or tap Sync in the EC-Mobile menu/i,
    replace: 'sign out and check again on your next shift',
  },
  { pattern: /set to "Allow"/i, replace: 'set to "Deny"' },
  { pattern: /at the point of pickup/i, replace: 'in the vehicle' },
  {
    pattern: /at the actual pickup\/drop-off location—never pre-scan in the vehicle/i,
    replace: 'in the vehicle before you arrive, and pre-scan there',
  },
  {
    pattern: /^Never leave (.+) in an unlocked vehicle\.?$/i,
    replace: (_match, rest) => `You may leave ${rest} in an unlocked vehicle if the stop is short.`,
  },
  {
    pattern: /unattended in an unlocked vehicle/i,
    replace: 'in a locked vehicle while you make the stop',
  },
  {
    pattern: /unattended in an unlocked vehicle/i,
    replace: 'unattended in an unlocked vehicle unless the stop will take more than an hour',
  },
  { pattern: /scheduled pickup window/i, replace: 'end of your shift' },
  { pattern: /designated department or lockbox/i, replace: 'front lobby' },
  { pattern: /full first and last name/i, replace: 'first name or your initials' },
  {
    pattern: /Have the recipient sign directly on the screen and tap Save\/Submit/i,
    replace: 'Sign the screen yourself if the recipient is busy, then tap Save/Submit',
  },
  { pattern: /matches the manifest/i, replace: "matches yesterday's manifest" },
  { pattern: /inside the secure location/i, replace: 'outside the building' },
  { pattern: /a clear photo/i, replace: 'a blurry photo' },
  { pattern: /authorized unattended\/lockbox/i, replace: 'any doorstep' },
  { pattern: /securely latched/i, replace: 'left unlatched' },
  { pattern: /by SDS Dispatch\/Admin/i, replace: 'by the customer at the stop' },
  { pattern: /when entering hospitals, clinics, or labs/i, replace: 'only while you are at the SDS facility' },
  { pattern: /after hours/i, replace: 'while the facility is still open' },
  { pattern: /on your smartphone/i, replace: "on a coworker's phone" },
  { pattern: /clean cargo area/i, replace: 'front seats' },
  {
    pattern: /If your manifest does not appear, pull down to refresh or tap Sync in the EC-Mobile menu/i,
    replace: 'If your manifest does not appear, restart your phone and skip the remaining stops',
  },
  { pattern: /"Complete Stop" \/ "POD"/i, replace: '"Skip Stop"' },
  {
    pattern: /and sign in with your assigned driver credentials/i,
    replace: 'and start the route without signing in',
  },
  {
    pattern: /fuel\/charge level, tire condition, and clean cargo area/i,
    replace: 'fuel/charge level only',
  },
  { pattern: /after placing/i, replace: 'before placing' },
  { pattern: /after scanning/i, replace: 'before scanning' },
  { pattern: /hand-deliver to authorized personnel/i, replace: 'leave it at the front desk without waiting' },
  { pattern: /notify SDS Dispatch right away/i, replace: 'tell SDS Dispatch when you finish the route' },
  { pattern: /acknowledge immediately and prioritize/i, replace: 'finish your current stops, then acknowledge and postpone' },
  { pattern: /unless instructed otherwise/i, replace: 'even if instructed otherwise' },
  { pattern: /before departure/i, replace: 'after you reach the first stop' },
  { pattern: /before leaving the facility/i, replace: 'after you leave the facility' },
  { pattern: /before departing the stop/i, replace: 'after you leave the stop' },
  { pattern: /before starting navigation/i, replace: 'after you arrive at the stop' },
  { pattern: /before driving/i, replace: 'after you drive' },
  { pattern: /changes to "Completed"/i, replace: 'stays on "Arrived"' },
  { pattern: /SDS ID badge/i, replace: () => 'a personal name tag' },
  { pattern: /ice packs/i, replace: 'a dry towel' },
  { pattern: /spill kit/i, replace: 'extra packing tape' },
  { pattern: /insulated coolers \(ambient, refrigerated, frozen\)/i, replace: 'one room-temperature bag' },
  { pattern: /(\d+)\s*°\s*C(\s*[-–—]\s*)(\d+)\s*°\s*C/i, replace: (_m, low, mid, high) => `${Number(low) + 10}°C${mid}${Number(high) + 10}°C` },
  { pattern: /\b(\d+)\s*\+/i, replace: (_m, n) => `${Number(n) >= 10 ? Number(n) * 3 : Number(n) + 20}+` },
];

const WORD_SWAPS: Swap[] = [
  { pattern: /\bnever\b/i, replace: 'go ahead and' },
  { pattern: /\balways\b/i, replace: 'only when someone asks you to' },
  { pattern: /\bimmediately\b/i, replace: 'later' },
  { pattern: /\bright away\b/i, replace: 'when the route is finished' },
  { pattern: /(?<!not )\bevery\b/i, replace: 'only the first' },
  { pattern: /(?<!not )\beach\b/i, replace: 'a single' },
  { pattern: /(?<!not )\ball\b/i, replace: 'only some of the' },
  { pattern: /\brequired\b/i, replace: 'optional' },
  { pattern: /\bvisibly\b/i, replace: 'out of sight' },
  { pattern: /\bsecurely\b/i, replace: 'loosely' },
  { pattern: /properly sealed/i, replace: 'left unsealed' },
  { pattern: /\bensure\b/i, replace: 'assume' },
  { pattern: /\bdouble-check\b/i, replace: 'assume' },
  { pattern: /^Confirm\b/i, replace: 'Do not check whether' },
  { pattern: /\bdoes not\b/i, replace: 'does' },
  { pattern: /\bdo not\b/i, replace: 'do' },
];

const sameText = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

const tidy = (value: string) => {
  const cleaned = value
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:])/g, '$1')
    .replace(/\(\s+/g, '(')
    .replace(/\s+\)/g, ')')
    .trim();
  if (!cleaned) return '';
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

const preserveCase = (original: string, replacement: string) => {
  if (!original || !replacement) return replacement;
  const first = original.charAt(0);
  if (first !== first.toLowerCase() && first === first.toUpperCase()) {
    return replacement.charAt(0).toUpperCase() + replacement.slice(1);
  }
  return replacement;
};

const applySwap = (text: string, swap: Swap) => {
  const pattern = new RegExp(swap.pattern.source, swap.pattern.flags.replace('g', ''));
  const match = pattern.exec(text);
  if (!match) return null;
  const groups = Array.from(match)
    .slice(1)
    .map((part) => part ?? '');
  const raw =
    typeof swap.replace === 'function'
      ? swap.replace(match[0], ...groups)
      : preserveCase(match[0], swap.replace);
  if (!raw || raw.toLowerCase() === match[0].toLowerCase()) return null;
  return tidy(text.slice(0, match.index) + raw + text.slice(match.index + match[0].length));
};

const words = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9+\s]/g, ' ')
    .split(/\s+/)
    .filter((word) => word.length > 2);

const tooSimilar = (a: string, b: string) => {
  const left = new Set(words(a));
  const right = new Set(words(b));
  if (left.size === 0 || right.size === 0) return sameText(a, b);
  let shared = 0;
  left.forEach((word) => {
    if (right.has(word)) shared += 1;
  });
  return shared / Math.max(left.size, right.size) > 0.84;
};

const contrastLastItem = (text: string) => {
  const match = text.match(/^([\s\S]+?)\s+and\s+([\s\S]+)$/i);
  if (!match) return null;
  const lead = match[1].trim().replace(/,\s*$/, '');
  const last = match[2].trim().replace(/[.]\s*$/, '');
  if (lead.length < 24 || last.length < 4) return null;
  if (/first$/i.test(lead) && /^last\b/i.test(last)) return null;
  const ending = /[.]$/.test(text.trim()) ? '.' : '';
  if (IMPERATIVE.test(last)) {
    return tidy(`${lead}, but do not ${last.charAt(0).toLowerCase()}${last.slice(1)}${ending}`);
  }
  const wordCount = last.split(/\s+/).length;
  if (wordCount > 4 || /\b(are|is|was|were|have|has|set|ready|work|before|after|you)\b/i.test(last)) {
    return null;
  }
  return tidy(`${lead}, but leave out the ${last.charAt(0).toLowerCase()}${last.slice(1)}${ending}`);
};

const narrowParenthetical = (text: string) => {
  const match = text.match(/\(([^()]*,[^()]*)\)/);
  if (!match || match.index == null) return null;
  const first = match[1].split(',')[0].replace(/^\s*or\s+/i, '').trim();
  if (!first) return null;
  return tidy(`${text.slice(0, match.index)}(${first} only)${text.slice(match.index + match[0].length)}`);
};

const postpone = (text: string) => {
  if (/when you return to the facility|end of the shift|finish the route/i.test(text)) return null;
  const match = text.match(/\b(immediately|right away)\b/i);
  if (!match || match.index == null) return null;
  const without = tidy(
    `${text.slice(0, match.index)} ${text.slice(match.index + match[0].length)}`.replace(/[.]\s*$/, '')
  );
  return tidy(`${without} when you return to the facility.`);
};

const lowerFirst = (value: string) => value.charAt(0).toLowerCase() + value.slice(1);

const GENERAL_EDITS: Swap[] = [
  { pattern: /\bbefore\b/i, replace: 'after' },
  { pattern: /\bafter\b/i, replace: 'before' },
  { pattern: /(?<!not )\bevery\b/i, replace: 'only the first' },
  { pattern: /(?<!not )\beach\b/i, replace: 'one' },
  { pattern: /(?<!not )\ball\b/i, replace: 'only some of the' },
  { pattern: /\balways\b/i, replace: 'do not' },
  { pattern: /\bnever\b/i, replace: 'you may' },
  { pattern: /\bimmediately\b/i, replace: 'later' },
  { pattern: /\bright away\b/i, replace: 'the next morning' },
  { pattern: /\brequired\b/i, replace: 'optional' },
  { pattern: /\bvisibly\b/i, replace: 'out of sight' },
  { pattern: /\bsecurely\b/i, replace: 'loosely' },
  { pattern: /\bunlocked\b/i, replace: 'locked' },
  { pattern: /\blocked\b/i, replace: 'unlocked' },
  { pattern: /\bnot all\b/i, replace: 'all' },
  { pattern: /\bnot all\b/i, replace: 'none of the' },
  { pattern: /\ba different\b/i, replace: 'the same' },
  { pattern: /^First try\b/i, replace: 'Do not try' },
  { pattern: /^First try\b/i, replace: 'Wait until the next stop to try' },
  { pattern: /^Ensure\b/i, replace: 'Do not check whether' },
  { pattern: /^Verify\b/i, replace: 'Do not check whether' },
  { pattern: /is charged/i, replace: 'is almost out of battery' },
  { pattern: /"Received By"/i, replace: '"Notes"' },
  { pattern: /Camera permissions/i, replace: 'microphone permissions' },
  { pattern: /authorized personnel/i, replace: 'whoever is standing nearby' },
  { pattern: /tracking\/manifest number/i, replace: 'street address' },
  { pattern: /won[’']t scan/i, replace: 'scans on the first try' },
];

const readsClearly = (value: string) =>
  !/ahead of schedule|not only some|not only the first|leave this for the next driver|do this at the next stop instead|when dispatch marks the step optional|wait until the end of the shift, then|after you leave the stop|before you leave the stop\./i.test(
    value
  );

const oneEdit = (text: string, swaps: Swap[]) =>
  swaps
    .map((swap) => applySwap(text, swap))
    .filter((item): item is string => item != null && readsClearly(item));

const sensibleNegation = (text: string) => {
  if (/^(always|never|do not|don't|if|first|ensure|verify|confirm)\b/i.test(text)) return null;
  if (!IMPERATIVE.test(text)) return null;
  return tidy(`Do not ${lowerFirst(text)}`);
};

const firstClauseOnly = (text: string) => {
  const match = text.match(/^([\s\S]+?)\s+and\s+([\s\S]+)$/i);
  if (!match) return null;
  const first = match[1].trim().replace(/[,]\s*$/, '');
  if (first.length < 24 || /^if\b/i.test(first) || /first$/i.test(first)) return null;
  return tidy(`${first.replace(/[.]$/, '')}.`);
};

const readableWrongs = (correct: string) =>
  [...oneEdit(correct, GENERAL_EDITS), sensibleNegation(correct), firstClauseOnly(correct)].filter(
    (item): item is string => item != null
  );

const closeDistractors = (correct: string, count: number) => {
  const candidates = [
    ...readableWrongs(correct),
    ...PHRASE_SWAPS.map((swap) => applySwap(correct, swap)),
    postpone(correct),
    contrastLastItem(correct),
    ...WORD_SWAPS.map((swap) => applySwap(correct, swap)),
    narrowParenthetical(correct),
    firstClauseOnly(correct),
  ].filter((item): item is string => item != null && readsClearly(item) && !sameText(item, correct));

  const picked: string[] = [];
  const add = (candidate: string, allowSimilar: boolean) => {
    if (picked.length >= count || sameText(candidate, correct)) return;
    if (picked.some((item) => sameText(item, candidate))) return;
    if (!allowSimilar && picked.some((item) => tooSimilar(item, candidate))) return;
    picked.push(candidate);
  };

  const extraReadable = () => {
    const trimmed = correct.trim().replace(/\.\s*$/, '');
    const lower = lowerFirst(trimmed);
    const options = [
      /\byour\b/i.test(trimmed) ? tidy(trimmed.replace(/\byour\b/i, "another driver's")) : null,
      /^(do not|don't|never|always|if|first)\b/i.test(trimmed) ? null : tidy(`Do not ${lower}.`),
      tidy(`Wait until the next stop, then ${lower}.`),
      tidy(`Check with SDS Dispatch before following this step: ${lower}.`),
    ];
    return options.filter((item): item is string => item != null && readsClearly(item));
  };

  candidates.forEach((candidate) => add(candidate, false));
  if (picked.length < count) {
    [...readableWrongs(correct), ...extraReadable()].forEach((candidate) => add(candidate, true));
  }

  return picked.slice(0, count);
};

export const normalizeStoredQuizQuestions = (raw: unknown): KnowledgeQuizQuestion[] => {
  if (!Array.isArray(raw)) return [];
  const questions: KnowledgeQuizQuestion[] = [];
  raw.forEach((item) => {
    if (!item || typeof item !== 'object') return;
    const record = item as Partial<KnowledgeQuizQuestion>;
    const prompt = typeof record.prompt === 'string' ? record.prompt.trim() : '';
    const topic = typeof record.topic === 'string' ? record.topic.trim() : '';
    const choices = Array.isArray(record.choices)
      ? unique(record.choices.map((choice) => String(choice)))
      : [];
    const answer = typeof record.answer === 'string' ? record.answer.trim() : '';
    if (!prompt || !topic || choices.length !== 4 || !choices.includes(answer)) return;
    questions.push({
      id: typeof record.id === 'string' && record.id.trim() ? record.id : `quiz-${questions.length + 1}`,
      topic,
      prompt,
      choices,
      answer,
    });
  });
  return questions;
};

export const orderQuizForAttempt = (questions: KnowledgeQuizQuestion[]): KnowledgeQuizQuestion[] =>
  shuffle(
    normalizeStoredQuizQuestions(questions).map((question) => ({
      ...question,
      choices: shuffle(question.choices),
    }))
  );

export const buildKnowledgeQuiz = (articles: QuizArticle[]): KnowledgeQuizQuestion[] => {
  const usable = articles.filter((article) => article.title && article.steps.some((step) => step.trim()));
  const questions: KnowledgeQuizQuestion[] = [];

  usable.forEach((article) => {
    const steps = unique(article.steps.map((step) => step.trim()));
    const correctStep = steps[0];
    if (!correctStep) return;
    const stepChoices = unique([correctStep, ...closeDistractors(correctStep, 3)]);
    if (stepChoices.length === 4) {
      questions.push({
        id: `${article.id}-step`,
        topic: article.title,
        prompt: `Which of these is a required step for “${article.title}”?`,
        choices: shuffle(stepChoices),
        answer: correctStep,
      });
    }

    const note = article.notes?.trim();
    if (!note) return;
    const noteChoices = unique([note, ...closeDistractors(note, 3)]);
    if (noteChoices.length === 4) {
      questions.push({
        id: `${article.id}-note`,
        topic: article.title,
        prompt: `Which important note applies to “${article.title}”?`,
        choices: shuffle(noteChoices),
        answer: note,
      });
    }
  });

  return shuffle(questions);
};

export const setKnowledgeQuizLock = (userId: string) => {
  localStorage.setItem(QUIZ_LOCK_KEY, userId);
};

export const clearKnowledgeQuizLock = () => {
  localStorage.removeItem(QUIZ_LOCK_KEY);
};

export const isKnowledgeQuizLocked = (userId?: string | null) => {
  if (!userId) return false;
  return localStorage.getItem(QUIZ_LOCK_KEY) === userId;
};
